export interface PatientRequestDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

export type PatientRequestDateInput = string | Date | null | undefined;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function isValidDateParts(parts: PatientRequestDateTimeParts): boolean {
  if (parts.hour < 0 || parts.hour > 23 || parts.minute < 0 || parts.minute > 59) {
    return false;
  }
  const d = new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0);
  return (
    !Number.isNaN(d.getTime()) &&
    d.getFullYear() === parts.year &&
    d.getMonth() === parts.month - 1 &&
    d.getDate() === parts.day &&
    d.getHours() === parts.hour &&
    d.getMinutes() === parts.minute
  );
}

function partsFromDate(d: Date): PatientRequestDateTimeParts | null {
  if (Number.isNaN(d.getTime())) return null;
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function partsFromIsoLike(raw: string): PatientRequestDateTimeParts | null {
  const isoLike = /^(\d{4})[-.](\d{1,2})[-.](\d{1,2})(?:[T\s]+(\d{1,2}):(\d{2}))?/.exec(raw);
  if (!isoLike) return null;
  const parts = {
    year: Number(isoLike[1]),
    month: Number(isoLike[2]),
    day: Number(isoLike[3]),
    hour: isoLike[4] == null ? 0 : Number(isoLike[4]),
    minute: isoLike[5] == null ? 0 : Number(isoLike[5]),
  };
  return isValidDateParts(parts) ? parts : null;
}

function partsFromDdMmYyyy(raw: string): PatientRequestDateTimeParts | null {
  const ddMmYyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T\s]+(\d{1,2}):(\d{2}))?/.exec(raw);
  if (!ddMmYyyy) return null;
  const parts = {
    year: Number(ddMmYyyy[3]),
    month: Number(ddMmYyyy[2]),
    day: Number(ddMmYyyy[1]),
    hour: ddMmYyyy[4] == null ? 0 : Number(ddMmYyyy[4]),
    minute: ddMmYyyy[5] == null ? 0 : Number(ddMmYyyy[5]),
  };
  return isValidDateParts(parts) ? parts : null;
}

function hasPatientRequestDateShape(raw: string): boolean {
  return (
    /^\d{4}[-.]\d{1,2}[-.]\d{1,2}(?:[T\s]+\d{1,2}:\d{2})?/.test(raw) ||
    /^\d{1,2}\/\d{1,2}\/\d{4}(?:[T\s]+\d{1,2}:\d{2})?/.test(raw)
  );
}

export function parsePatientRequestDateTime(value: PatientRequestDateInput): PatientRequestDateTimeParts | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return partsFromDate(value);

  const raw = String(value).trim();
  if (!raw) return null;

  const parsedTextualParts = partsFromIsoLike(raw) ?? partsFromDdMmYyyy(raw);
  if (parsedTextualParts) return parsedTextualParts;
  if (hasPatientRequestDateShape(raw)) return null;

  return partsFromDate(new Date(raw));
}

export function formatPatientRequestDateTime(value: PatientRequestDateInput, yearDigits: 2 | 4 = 4): string {
  const parts = parsePatientRequestDateTime(value);
  if (!parts) return value == null ? '' : String(value);
  const year = yearDigits === 2 ? String(parts.year).slice(-2) : String(parts.year);
  return `${pad(parts.day)}/${pad(parts.month)}/${year} ${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function formatPatientRequestDateOnly(value: PatientRequestDateInput): string {
  const parts = parsePatientRequestDateTime(value);
  if (!parts) return value == null ? '' : String(value);
  return `${pad(parts.day)}/${pad(parts.month)}/${parts.year}`;
}

export function patientRequestDateOnlyForFilter(value: PatientRequestDateInput): Date {
  const parts = parsePatientRequestDateTime(value);
  if (!parts) return new Date(Number.NaN);
  return new Date(parts.year, parts.month - 1, parts.day);
}
